<template>

	<InsightsDrawer v-if="showWeekInsights"
		:title="$t('Insights') + ' - ' + $t('Wk') + calendarDateObject.toFormat('WW / kkkk')"
		sessionStateKey="insights.week"
		:defaultDateRange="weekAsInterval"
		:showPupilSelector="true"
		:showDateRangeSelector="false"
		:insights="[
						{ value: 'activity', label: this.$tc('Activity', 2) },
						{ value: 'feedback', label: this.$t('Feedback') },
					]"
		@requestClose="() => { this.showWeekInsights = null; }"
	/>
	<AgendaGoalEvaluation
		v-if="showGoalEvaluation"
		:title="$tc('Goal', 2) + ' - ' + $t('Wk') +  calendarDateObject.toFormat('WW / kkkk')"
		:dateRange="weekAsInterval"
		@requestClose="showGoalEvaluation = null"
	/>

	<div id="week-toolbar" class="flex justify-between items-center mb-4 border-b pt-4 pb-2 bg-base">
		<div class="flex gap-4 items-center">
			<div class="flex">
				<div class="pr-1 pt-1 flex flex-col items-end">
					<span class="block font-bold leading-3 lowercase" :class="[ isPast ? 'text-base-content-light' : '',]">{{ $t('Wk') }}</span>
					<span class="block text-xs" :class="[ isPast ? 'text-base-content-light' : '',]">{{ calendarDateObject.toFormat('kkkk') }}</span>
				</div>
				<div class="font-bold text-3xl" :class="[ isPast ? 'text-base-content-light' : '',]">{{ calendarDateObject.toFormat('WW') }}</div>
			</div>
			<div class="shrink-0 flex items-center print:hidden">
				<button class="btn btn-ghost bg-base-100 btn-sm rounded-r-none border-r-0" @click="goToPrev()">
					<ChevronLeftIcon class="w-4 h-4"/>
				</button>
				<button class="btn btn-ghost bg-base-100 btn-sm rounded-l-none" @click="goToNext()">
					<ChevronRightIcon class="w-4 h-4"/>
				</button>
			</div>

			<div class="hidden sm:flex" id="ag-head-dp-wrapper">
				<DateSelector
					v-bind="datePickerDate"
					:asInput="false"
					class="print:hidden"
					:disabled-week-days="[6, 0]"
					@update:model-value="goToSelectedDate"
					:preset-dates="dateSelectorPresetDates()"
				/>
			</div>
		</div>


		<div class="flex gap-1 justify-end items-center print:hidden">

			<button v-if="canShowGoalEvaluations" @click="showGoalEvaluation = true" :disabled="!hasLessons" class="btn btn-ghost bg-base-100 btn-sm hidden md:block" :title="$t('Evaluate goals')">
				<MapPinIcon class="w-5 h-5"/>
			</button>

			<button @click="showWeekInsights = true" :disabled="!isCurrentWeekOrPast || !hasLessons" class="btn btn-ghost bg-base-100 btn-sm hidden md:block" :title="$t('Insights')">
				<ChartBarIcon class="w-5 h-5" />
			</button>

			<button @click="addWeekToClipboard()" :disabled="!hasLessons" class="btn btn-ghost bg-base-100 btn-sm" :title="$t('Add week to clipboard')">
				<DocumentDuplicateIcon class="w-5 h-5" />
			</button>

			<Dropdown ref="clipboardDropdown">
				<template #trigger>
					<button class="btn btn-ghost bg-base-100 btn-sm indicator"  :disabled="!availableClipboardItems.length" :title="$t('Paste')">
						<span v-show="availableClipboardItems.length" class="indicator-item badge bg-danger border-none text-white">{{ availableClipboardItems.length }}</span>
						<ClipboardDocumentListIcon class="w-5 h-5" />
					</button>
				</template>
				<template #content>
					<DropdownMenu class="w-max">
						<DropdownMenuItem :disabled="!isCurrentWeekOrPast || !hasLessons" class="block md:hidden">
							<a @click="showWeekInsights = true">
								<ChartBarIcon class="w-4 h-4"/>
								{{ $t('Insights') }}
							</a>
						</DropdownMenuItem>

						<DropdownMenuItem v-for="(item, index) in availableClipboardItems" :key="index">
							<div class="py-4 px-4 flex flex-col gap-y-1">

								<div class="flex items-center">
									<div>
										<div class="flex">
										<div class="w-28">
											<span class="text-base-content-light">{{item.interval.start.year}} /</span>
											<span class="font-bold">{{$t('Wk')}} {{item.interval.start.weekNumber}}</span>
										</div>
										<div class="w-28">
											{{item.interval.start.toLocaleString({ day: '2-digit', month: 'short' })}} - {{item.interval.end.toLocaleString({ day: '2-digit', month: 'short' })}}
										</div>

										</div>

										<div v-if="item.isFromDifferentClassroom" class="alert alert-warning pt-1 pb-1 pl-2 pr-2">
											{{ $t('Copy from {classroom}', { classroom: item.classroomName }) }}
										</div>
									</div>

									<div class="w-40 flex items-center justify-end gap-x-2">
										<button :disabled="pastingFromClipboard || isCompletelyPast" type="button" class="btn btn-sm btn-primary" @click="pasteFromClipboard(item)">{{ $t('Paste') }}</button>
										<button class="btn btn-ghost btn-circle btn-xs" @click="removeFromClipboard(item)">
											<XMarkIcon class="w-5 h-5"/>
										</button>
									</div>
								</div>
								<div class="flex gap-x-1">
									<Tag v-for="themeName in item.themeNames" class="tag-theme tag-xs">
										{{themeName }}
									</Tag>
								</div>

							</div>
						</DropdownMenuItem>
						<DropdownMenuItem class="border-t" v-show="availableClipboardItems.length">
							<div class="py-4 px-4 flex justify-end">
								<button type="button" class="btn btn-sm btn-outline" @click="clipboardStore.clear()">{{ $t('Clear clipboard') }}</button>
							</div>
						</DropdownMenuItem>
					</DropdownMenu>
				</template>
			</Dropdown>

			<button @click="clearWeek" :disabled="!hasLessons" class="btn btn-ghost bg-base-100 btn-sm hidden md:block ml-2" :title="$t('Clear week')">
				<TrashIcon class="w-5 h-5" />
			</button>

			<Dropdown>
				<template #trigger>
					<button class="btn btn-ghost bg-base-100 btn-sm ml-2 md:hidden">
						<EllipsisVerticalIcon class="w-5 h-5"/>
					</button>
				</template>
				<template #content>
					<DropdownMenu class="w-48">
            <!-- temporary hidden
						<DropdownMenuItem>
							<a href="">
								<PrinterIcon class="w-4 h-4"/>
								<span>{{ $t('Print week') }}</span>
							</a>
						</DropdownMenuItem>
						-->

						<DropdownMenuItem v-if="canShowGoalEvaluations" :disabled="!hasLessons">
							<a @click="showGoalEvaluation = true">
								<MapPinIcon class="w-4 h-4"/>
								{{ $t('Evaluate goals') }}
							</a>
						</DropdownMenuItem>

						<DropdownMenuItem :disabled="!isCurrentWeekOrPast || !hasLessons" class="block">
							<a @click="showWeekInsights = true">
								<ChartBarIcon class="w-4 h-4"/>
								{{ $t('Insights') }}
							</a>
						</DropdownMenuItem>
						<DropdownMenuItem :disabled="!hasLessons" class="block">
							<a @click="clearWeek">
								<TrashIcon class="w-4 h-4"/>
								{{ $t('Clear week') }}
							</a>
						</DropdownMenuItem>
					</DropdownMenu>
				</template>
			</Dropdown>
		</div>
	</div>

	<Modal v-if="showClearWeekConfirmation" @requestUnmount="closeClearWeekConfirmation">
		<template #header>&nbsp;</template>

		<template #default="modalActions">
			<div class="py-6">
				{{ $t('Are you sure you want to delete the entire week\'s schedule?') }}

				<template v-if="lessonsStore.errors?.generic">
					<div class="alert alert-error mb-4 w-full sm:max-w-2xl m-auto">
						{{ lessonsStore.errors?.generic.toLocaleString($i18n) }}
					</div>
				</template>
			</div>


			<div class="modal-footer flex justify-end gap-1">
				<button class="btn btn-ghost" @click="modalActions.hide">{{ $t('Cancel') }}</button>
				<button class="btn btn-danger" @click="confirmClearWeek()" :class="[ clearingWeek ? 'loading' : '' ]">{{ clearingWeek ? '' : $t('Clear week') }}</button>
			</div>
		</template>
	</Modal>

	<Modal v-if="showOutdatedConfirmation" @requestUnmount="closeConfirmOverridePaste">
		<template #header>&nbsp;</template>

		<template #default="modalActions">
			<p class="py-2">
				{{ $t('#error: agenda-outdated-lesson-plans') }}
			</p>

			<div class="flex flex-col justify-end gap-2 py-4">
				<label class="flex items-center gap-2">
					<input type="radio" class="radio radio-sm bg-white border-neutral" v-model="updateOutdatedLessonPlans" :value="true" />
					{{ $t('Update lesson plans') }}
				</label>
				<label class="flex items-center gap-2">
					<input type="radio" class="radio radio-sm bg-white border-neutral" v-model="updateOutdatedLessonPlans" :value="false" />
					{{ $t('Keep old lesson plans') }}
				</label>

			</div>
			<div class="modal-footer flex justify-end gap-1">
				<button class="btn btn-primary" @click="confirmOutdatedLessonPlanStrategy()" :disabled="pastingFromClipboard" :class="[ pastingFromClipboard ? 'loading' : '' ]">{{ pastingFromClipboard ? '' : $t('Confirm') }}</button>
			</div>
		</template>
	</Modal>

	<Modal v-if="showOverridePasteConfirmation && !showOutdatedConfirmation" @requestUnmount="closeConfirmOverridePaste">
		<template #header>&nbsp;</template>

		<template #default="modalActions">
			{{ $t('This week already contains a schedule. Are you sure you want to overwrite it?') }}

			<div class="modal-footer flex justify-end gap-1">
				<button class="btn btn-ghost" @click="modalActions.hide" :disabled="pastingFromClipboard">{{ $t('Cancel') }}</button>
				<button class="btn btn-danger" @click="confirmPasteFromClipboard()" :disabled="pastingFromClipboard" :class="[ pastingFromClipboard ? 'loading' : '' ]">{{ pastingFromClipboard ? '' : $t('Overwrite schedule') }}</button>
			</div>
		</template>
	</Modal>

	<Modal v-if="showCopyAcrossOrganisationWarning" @requestUnmount="closeCopyAcrossOrganisationWarning">

		<template #header>&nbsp;</template>

		<template #default="modalActions">

			{{ $t('#notification: consequences of copying from another classroom') }}

			<div class="pt-5 pl-3 pr-3 pb-5 flex gap-2">
				<input type="checkbox" class="checkbox checkbox-sm" id="copy-cross-classroom-confirmation" v-model="confirmCopyAcrossOrganisation" />
				<label for="copy-cross-classroom-confirmation">
					{{ $t('I understand this will copy activities, lesson plans, goals, files, ... from {sourceClassroom} to {destinationClassroom}.', {
	 					sourceClassroom: selectedCopySelection?.classroomName,
						destinationClassroom: classroomsStore.getCurrentClassroom().name
					}) }}
				</label>
			</div>

			<div class="modal-footer flex justify-end gap-1">
				<button class="btn btn-ghost" @click="modalActions.hide" :disabled="pastingFromClipboard">{{ $t('Cancel') }}</button>
				<button class="btn btn-danger" @click="confirmPasteFromClipboard()" :disabled="pastingFromClipboard || !confirmCopyAcrossOrganisation" :class="[ pastingFromClipboard ? 'loading' : '' ]">
					{{ $t('Copy from different classroom') }}
				</button>
			</div>
		</template>

	</Modal>

</template>

<style>
#week-toolbar button.btn:disabled {
	@apply opacity-75 bg-base-100;
}
/* bit hacky, but vue-tailwind-datepicker doesn't support a 'button without input' scenario very well */
div#ag-head-dp-wrapper div#vtd > div > div {
	@apply w-80;
}
</style>

<script lang="ts">
import {DateTime, Interval} from "luxon";
import Dropdown from './ui/Dropdown.vue';
import DropdownMenu from "./ui/DropdownMenu.vue";
import DropdownMenuItem from "./ui/DropdownMenuItem.vue";
import DateSelector from "@/components/ui/DateSelector.vue";
import {
	ChevronLeftIcon, ChevronRightIcon, DocumentDuplicateIcon, PrinterIcon, ViewfinderCircleIcon,
	CalendarDaysIcon, EllipsisVerticalIcon,QuestionMarkCircleIcon, ClipboardDocumentListIcon,
	PencilIcon, PlusIcon, ChartBarIcon, XMarkIcon, TrashIcon,
MapPinIcon,
} from '@heroicons/vue/24/outline';
import {mapState, mapStores} from "pinia";
import {useLessonsStore} from "@/stores/Lessons.store";
import Modal from "@/components/ui/Modal.vue";
import {ApiErrorMessageBag} from "@/stores/errors/ApiErrorMessageBag.ts";
import {useClipboardStore} from "@/stores/Clipboard.store";
import {CopySelection} from "@/models/CopySelection.model";
import {useThemesStore} from "@/stores/Themes.store";
import InsightsDrawer from '@/components/InsightsDrawer.vue';
import Tag from "@/components/ui/Tag.vue";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import AgendaGoalEvaluation from "@/components/AgendaGoalEvaluation.vue";
import {useClassroomsStore} from "@/stores/Classrooms.store.ts";

export default {
	props: {
		calendarIsoDate: {
			type: String,   // ISO format (yyyy-mm-dd)
			required: true,
		}
	},
	emits: [
		'calendarGoTo',
		'addThemePeriod',
		'pastedFromClipboard',
	],
	components: {
		Dropdown, DropdownMenu, DropdownMenuItem,
		DateSelector,
		ChevronLeftIcon, ChevronRightIcon, DocumentDuplicateIcon, PrinterIcon, ViewfinderCircleIcon,
		CalendarDaysIcon, EllipsisVerticalIcon, QuestionMarkCircleIcon, ClipboardDocumentListIcon,
		PencilIcon, PlusIcon, ChartBarIcon, XMarkIcon, TrashIcon,
		Modal,
		InsightsDrawer,
		Tag,
		MapPinIcon,
		AgendaGoalEvaluation,
	},
	data() {
		return {
			showClearWeekConfirmation: false,
			showOverridePasteConfirmation: false,
			showCopyAcrossOrganisationWarning : false,
			confirmCopyAcrossOrganisation: false,
			showOutdatedConfirmation: false,
			updateOutdatedLessonPlans: null as null | boolean,
			selectedCopySelection: null as CopySelection | null,
			clearingWeek: false,
			pastingFromClipboard: false,
			showWeekInsights: false,
			showGoalEvaluation: false,
			datePickerDate: null as Date | null,	// not really used, but needed to be able to use the update:model-value event
		}
	},
	computed: {
		...mapStores(useLessonsStore, useThemesStore, useOrganisationsStore, useClassroomsStore),
		...mapStores(useClipboardStore),
		...mapState(useClipboardStore, [ 'availableClipboardItems'] ),

		calendarDateObject() {
			return DateTime.fromISO(this.calendarIsoDate);
		},

		isCurrentWeekOrPast(){
			return this.weekAsInterval.contains(DateTime.local()) || this.isCompletelyPast;
		},

		isPast() {
			return this.calendarDateObject.endOf('week') < DateTime.local().startOf('day');
		},

		isCompletelyPast() {
			return this.calendarDateObject.startOf('week') < DateTime.local().endOf('day').minus({days: 7});
		},

		weekAsInterval() {
			return Interval.fromDateTimes(
				this.calendarDateObject.startOf('week'),
				this.calendarDateObject.endOf('week')
			);
		},

		hasLessons() {
			if (!this.calendarDateObject) {
				return false;
			}

			return this.lessonsStore.hasBetween(
				this.calendarDateObject.startOf('week').toJSDate(),
				this.calendarDateObject.endOf('week').toJSDate()
			);
		},

		canShowGoalEvaluations(): boolean {
			return !!this.organisationsStore.getCurrentOrganisation().curriculum;
		}
	},

	methods: {
		goToNext() {
			this.$emit('calendarGoTo', 'next');
		},
		goToPrev() {
			this.$emit('calendarGoTo', 'prev');
		},
		goToCurrent() {
			this.$emit('calendarGoTo', 'today');
		},
		goToSelectedDate(date: Date) {
			let dt = DateTime.fromJSDate(date);
			if (dt.weekday > 5) {
				// subtract 3 days to arrive midweek
				dt = dt.minus({ days: 3 });
			}

			this.$emit('calendarGoTo', 'date', dt);
		},

		async addWeekToClipboard() {

			if (!this.hasLessons) {
				this.$notify({
					text: this.$t('This week is empty'),
					type: "error",   // optional styling: info, success, error
				});
				return;
			}

			// Also add all themes to clipboard
			let themes = await this.themesStore.getBetween(
				this.calendarDateObject.startOf('week').toJSDate(),
				this.calendarDateObject.endOf('week').toJSDate()
			);

			this.clipboardStore.copy(Interval.fromDateTimes(
					this.calendarDateObject.startOf('week'),
					this.calendarDateObject.endOf('week')
			), themes.map(theme => theme.name));

			this.$notify({
				// title: "Klaar!",
				text: this.$t('This week was added to your clipboard'),
				type: "success",   // optional styling: info, success, error
			});
		},

		async pasteFromClipboard(copiedItem: CopySelection) {

			console.log('pasteFromClipboard: ', copiedItem);

			this.showOverridePasteConfirmation = false;
			this.confirmCopyAcrossOrganisation = false;
			this.showOutdatedConfirmation = false;
			this.updateOutdatedLessonPlans = null;

			this.selectedCopySelection = copiedItem;

			if (copiedItem.isFromDifferentClassroom) {
				this.showCopyAcrossOrganisationWarning = true;
				return;
			}

			await this._processPaste(this.selectedCopySelection);
		},

		async confirmPasteFromClipboard() {
			await this._processPaste(this.selectedCopySelection);
		},

		confirmOutdatedLessonPlanStrategy() {
			// updateOutdatedLessonPlans boolean already set with v-model
			this.showOutdatedConfirmation = false;
			if(!this.showOverridePasteConfirmation) {
				this._processPaste(this.selectedCopySelection);
			}
		},

		/**
		 * @param copiedItem
		 */
		async _processPaste(copiedItem: CopySelection) {

			if (this.availableClipboardItems.length === 0) {
				return;
			}

			// can't copy to the past.
			if (this.isCompletelyPast) {
				return;
			}

			this.pastingFromClipboard = true;

			let start = this.calendarDateObject.startOf('week');
			let end = start.plus(copiedItem.interval.toDuration());

			let sourceInterval = copiedItem.interval;
			let sourceClassroomId = copiedItem.classroomId;

			if (start.valueOf() < DateTime.local().valueOf()) {
				// The api doesn't like it when we paste in the past, so we need to make sure the destination is completely in the
				// future. And as the source duration must match the destination duration, we need to make sure of that too.
				start = DateTime.local().plus({ minutes: 10 });
				sourceInterval = Interval.fromDateTimes(
					sourceInterval.end.minus(Interval.fromDateTimes(start, end).toDuration()),
					sourceInterval.end
				);
			}

			const pasteDestination = Interval.fromDateTimes(start, end);
			try {

				await this.lessonsStore.copyTo(
					sourceClassroomId,
					sourceInterval,
					[ pasteDestination ],
					this.showOverridePasteConfirmation,
					this.updateOutdatedLessonPlans
				);
				this.showOutdatedConfirmation = false;
				this.updateOutdatedLessonPlans = null;

				this.showOverridePasteConfirmation = false;
				this.showCopyAcrossOrganisationWarning = false;

				this.$notify({
					// title: "Klaar!",
					text: this.$t('The schedule has been copied successfully.'),
					type: "success",   // optional styling: info, success, error
				});

				this.$emit('pastedFromClipboard');
				this.$refs.clipboardDropdown.close();

			} catch (e) {

				if (e.response?.status === 400) {
					// todo cleanup
                    const errors = e.response.data.errors[""];
                    errors.forEach(error => {
						switch (error.key) {
							case 'DESTINATION_NOT_EMPTY':
								this.showOverridePasteConfirmation = true;
								break;

							case 'OUTDATED_LESSON_PLANS':
								this.showOutdatedConfirmation = true;
								this.updateOutdatedLessonPlans = true; // default to updating
								break;

							default:
								throw e;
						}
                    });
				} else if (e instanceof ApiErrorMessageBag) {
					this.$notify({
						// title: "Klaar!",
						text: e.toString(),
						type: "error",   // optional styling: info, success, error
					});
				} else {
					throw e;
				}

			} finally {
				this.pastingFromClipboard = false;
			}

		},

		removeFromClipboard(item){
			this.clipboardStore.remove(item);
		},

		clearWeek() {
			this.showClearWeekConfirmation = true;
		},

		closeConfirmOverridePaste() {
			this.showOutdatedConfirmation = false;
			this.showOverridePasteConfirmation = false;
		},

		closeClearWeekConfirmation() {
			this.showClearWeekConfirmation = false;
		},

		closeCopyAcrossOrganisationWarning() {
			this.showCopyAcrossOrganisationWarning = false;
		},

		async confirmClearWeek() {
			if (this.clearingWeek) {
				return;
			}

			this.clearingWeek = true;

			try {
				await Promise.all([
					this.lessonsStore.clearWeek(this.calendarDateObject),
					this.themesStore.clearWeek(this.calendarDateObject)
				]);

				this.closeClearWeekConfirmation();
				this.clearingWeek = false;
			} catch (e) {
				if (!(e instanceof ApiErrorMessageBag)) {
					this.clearingWeek = false;
					throw e;
				}
			}
		},

		dateSelectorPresetDates() {
			const today = DateTime.local();
			const lastWeek = today.minus({ weeks: 1 });
			const lastYear = today.minus({ years: 1 });
			const currentWeekNo = today.weekNumber;

			return [
				{
					label: this.$t('This week'),
					value: today,
					slot: 'preset-date-button'
				},
				{
					label: this.$t('Last week'),
					value: lastWeek,
					slot: 'preset-date-button'
				},
				{
					label: this.$t('Wk') + ' ' + currentWeekNo + ', ' + lastYear.toFormat('kkkk'),
					value: lastYear,
					slot: 'preset-date-button'
				},
			];
		}
	},
}
</script>
